"use client";

// src/constants.ts
var GOLDEN_MAJOR = 0.6180339887498949;
var GOLDEN_MINOR = 1 - GOLDEN_MAJOR;

// src/SplitRegionContext.ts
import { createContext, useContext } from "react";
var DEFAULT_CONTEXT_VALUE = {
  share: 1,
  sizeRole: "equal",
  isGoldenRatio: false
};
var SplitRegionContext = createContext(DEFAULT_CONTEXT_VALUE);
function useSplitRegion() {
  return useContext(SplitRegionContext);
}

// src/Split.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function resolveFlexGrow(ratio, first) {
  if (ratio === "golden") {
    if (process.env.NODE_ENV === "development" && first === void 0) {
      console.warn(
        "[Split] ratio='golden' requires the `first` prop to be set ('major' or 'minor'). Defaulting to first='minor'. This will produce incorrect layout \u2014 fix the consumer."
      );
    }
    const resolvedFirst = first ?? "minor";
    return resolvedFirst === "major" ? [GOLDEN_MAJOR, GOLDEN_MINOR] : [GOLDEN_MINOR, GOLDEN_MAJOR];
  }
  if (ratio === "half") {
    return [0.5, 0.5];
  }
  if (ratio === "third") {
    return [1 / 3, 2 / 3];
  }
  if (process.env.NODE_ENV === "development") {
    if (ratio <= 0 || ratio >= 1) {
      console.warn(
        `[Split] numeric ratio must be between 0 and 1 exclusive. Received: ${ratio}. Layout will be incorrect.`
      );
    }
  }
  return [ratio, 1 - ratio];
}
function resolveRegionContexts(ratio, firstShare, secondShare) {
  const isGoldenRatio = ratio === "golden";
  let firstRole;
  let secondRole;
  if (firstShare === secondShare) {
    firstRole = "equal";
    secondRole = "equal";
  } else if (firstShare < secondShare) {
    firstRole = "minor";
    secondRole = "major";
  } else {
    firstRole = "major";
    secondRole = "minor";
  }
  return [
    { share: firstShare, sizeRole: firstRole, isGoldenRatio },
    { share: secondShare, sizeRole: secondRole, isGoldenRatio }
  ];
}
function Split({ axis, ratio, first, children, className = "" }) {
  const [firstChild, secondChild] = children;
  const [firstGrow, secondGrow] = resolveFlexGrow(ratio, first);
  const [firstRegionContext, secondRegionContext] = resolveRegionContexts(ratio, firstGrow, secondGrow);
  const containerStyle = {
    display: "flex",
    flexDirection: axis === "row" ? "column" : "row",
    width: "100%",
    height: "100%"
  };
  function regionStyle(flexGrow) {
    return {
      flexGrow,
      flexShrink: 0,
      flexBasis: 0,
      minWidth: 0,
      minHeight: 0,
      overflow: "hidden",
      position: "relative"
    };
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: containerStyle,
      className,
      "data-golden-split": "split",
      "data-axis": axis,
      "data-ratio": String(ratio),
      children: [
        /* @__PURE__ */ jsx("div", { style: regionStyle(firstGrow), "data-golden-split": "split-region", "data-region-index": 0, "data-region-share": firstGrow, children: /* @__PURE__ */ jsx(SplitRegionContext.Provider, { value: firstRegionContext, children: firstChild }) }),
        /* @__PURE__ */ jsx("div", { style: regionStyle(secondGrow), "data-golden-split": "split-region", "data-region-index": 1, "data-region-share": secondGrow, children: /* @__PURE__ */ jsx(SplitRegionContext.Provider, { value: secondRegionContext, children: secondChild }) })
      ]
    }
  );
}

// src/SplitThree.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function resolveFlexGrow2(pattern) {
  switch (pattern) {
    case "major-center": {
      const flank = GOLDEN_MINOR / 2;
      return [flank, GOLDEN_MAJOR, flank];
    }
    case "minor-center": {
      const flank = GOLDEN_MAJOR / 2;
      return [flank, GOLDEN_MINOR, flank];
    }
    case "equal": {
      const third = 1 / 3;
      return [third, third, third];
    }
  }
}
function resolveRegionContexts2(pattern, firstShare, centerShare, thirdShare) {
  const isGoldenRatio = pattern !== "equal";
  let flankRole;
  let centerRole;
  if (pattern === "equal") {
    flankRole = "equal";
    centerRole = "equal";
  } else if (centerShare > firstShare) {
    centerRole = "major";
    flankRole = "minor";
  } else {
    centerRole = "minor";
    flankRole = "major";
  }
  const firstContext = { share: firstShare, sizeRole: flankRole, isGoldenRatio };
  const centerContext = { share: centerShare, sizeRole: centerRole, isGoldenRatio };
  const thirdContext = { share: thirdShare, sizeRole: flankRole, isGoldenRatio };
  return [firstContext, centerContext, thirdContext];
}
function SplitThree({ axis, pattern, children, className = "" }) {
  const [firstChild, centerChild, thirdChild] = children;
  const [firstGrow, centerGrow, thirdGrow] = resolveFlexGrow2(pattern);
  const [firstRegionContext, centerRegionContext, thirdRegionContext] = resolveRegionContexts2(
    pattern,
    firstGrow,
    centerGrow,
    thirdGrow
  );
  const containerStyle = {
    display: "flex",
    flexDirection: axis === "row" ? "column" : "row",
    width: "100%",
    height: "100%"
  };
  function regionStyle(flexGrow) {
    return {
      flexGrow,
      flexShrink: 0,
      flexBasis: 0,
      minWidth: 0,
      minHeight: 0,
      overflow: "hidden",
      position: "relative"
    };
  }
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      style: containerStyle,
      className,
      "data-golden-split": "split-three",
      "data-axis": axis,
      "data-pattern": pattern,
      children: [
        /* @__PURE__ */ jsx2("div", { style: regionStyle(firstGrow), "data-golden-split": "split-three-region", "data-region-index": 0, "data-region-share": firstGrow, children: /* @__PURE__ */ jsx2(SplitRegionContext.Provider, { value: firstRegionContext, children: firstChild }) }),
        /* @__PURE__ */ jsx2("div", { style: regionStyle(centerGrow), "data-golden-split": "split-three-region", "data-region-index": 1, "data-region-share": centerGrow, children: /* @__PURE__ */ jsx2(SplitRegionContext.Provider, { value: centerRegionContext, children: centerChild }) }),
        /* @__PURE__ */ jsx2("div", { style: regionStyle(thirdGrow), "data-golden-split": "split-three-region", "data-region-index": 2, "data-region-share": thirdGrow, children: /* @__PURE__ */ jsx2(SplitRegionContext.Provider, { value: thirdRegionContext, children: thirdChild }) })
      ]
    }
  );
}
export {
  GOLDEN_MAJOR,
  GOLDEN_MINOR,
  Split,
  SplitRegionContext,
  SplitThree,
  useSplitRegion
};
//# sourceMappingURL=index.js.map